// Operator review of a living document: a project's briefing, or an operator's answers
// to an agent's questions. Mirrors lambda_handlers/api/stateful_agents/review/ and
// portfolio/briefing_versions.py. See docs/Y2026-Q4/design-review-mode.md.
import { APPLYING_POLL_MS, type ReviewPhase } from "./useDashboard";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

/** Where on the document a comment was made. Must agree with
 * review_comments.LEVELS. */
export type ReviewLevel =
  | "dashboard_row"
  | "specialist_summary"
  | "specialist_details"
  | "briefing"
  | "attributes"
  | "conversation_reply"
  | "agent_feedback"
  | "operator_change"
  | "automated_review"
  | "amazon_item"
  | "amazon_step"
  | "amazon_answer";

export interface ReviewAnchor {
  level: ReviewLevel;
  /** A human label for the block: "Grid > Status", "Email to UKPN". */
  block: string;
  /** The text the operator selected, if any. */
  quote: string;
  /** Text either side of it, to find the same occurrence again. */
  prefix: string;
  suffix: string;
  /** The block's text as it was rendered, for the agent applying the comment. */
  rendered?: string;
  /** A decision, email or question id when the comment is on one. */
  item_id: string;
  /** The specialist whose content it is on; "" for the dashboard row. */
  agent: string;
}

/** Only a project's briefing takes comments here: an answer to an agent's question is
 * sent from its Agent feedback row. */
export interface ReviewTarget {
  target: "project";
  project_id: string;
}

export type ReviewMode = "regenerate" | "discuss";

export interface ReviewComment {
  comment_id: string;
  target: "project" | "agent";
  project_id: string | null;
  agent: string | null;
  anchor: ReviewAnchor;
  body: string;
  author_email: string;
  briefing_version: number | null;
  mode: ReviewMode;
  conversation_id: string | null;
  run_id: string | null;
  status: "open" | "needs_input" | "actioned" | "declined" | "withdrawn";
  agreed_change: string | null;
  agreed_in_run_id: string | null;
  response: string | null;
  stored_in: string | null;
  settled_by: string | null;
  settled_by_run_id: string | null;
  settled_at: string | null;
  created_at: string;
}

export interface ReviewSubmitResult {
  /** "started", or "queued" behind the turn answering an earlier comment. */
  status: string;
  /** The review conversation the comment joined or started. */
  conversation_id: string;
  comment_ids: string[];
}


export type ReviewCommentsScope =
  { project_id: string } | { agent: string } | { run_id: string };

export interface ReviewCommentList {
  comments: ReviewComment[];
  /** The kind of every run a comment names, so it links to where that run lives. */
  run_kinds: Record<string, string>;
  /** What is happening to each comment in flight. */
  phases: Record<string, ReviewPhase>;
  /** "<run_id>:<project_id>" -> the agents that took part in the run that settled it. */
  worked_by: Record<string, string[]>;
}

export const useReviewComments = (scope: ReviewCommentsScope | null) => {
  const http = useAgentsHttp();
  return useQuery<ReviewCommentList>(
    [QK.reviewComments, scope],
    () => http<ReviewCommentList>("/review/comments", scope),
    // Comments move on while the operator watches; quicker while any is in flight.
    {
      enabled: scope !== null,
      refetchInterval: (data) =>
        data && Object.keys(data.phases ?? {}).length > 0
          ? APPLYING_POLL_MS
          : 15_000,
    },
  );
};

export const useSubmitReview = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    ReviewSubmitResult,
    Error,
    ReviewTarget & {
      comments: { anchor: ReviewAnchor; body: string }[];
      briefing_version: number | null;
      actor_email: string;
    }
  >((body) => http<ReviewSubmitResult>("/review/submit", body), {
    // The pages that show a pass applying it start watching for it to finish.
    onSuccess: () => {
      void qc.invalidateQueries([QK.reviewComments]);
      void qc.invalidateQueries([QK.dashboard]);
      void qc.invalidateQueries([QK.projectAgentStatus]);
      void qc.invalidateQueries([QK.agentAttention]);
    },
  });
};

/** Takes back a review comment still queued behind its conversation's running turn; an
 * answer to an agent's question goes back to waiting on an answer. */
export const useWithdrawReviewComment = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { comment_id: string; actor_email: string }
  >((body) => http("/review/comments/withdraw", body), {
    onSettled: () => {
      void qc.invalidateQueries([QK.reviewComments]);
      void qc.invalidateQueries([QK.feedbackQuestions]);
    },
  });
};

export interface BriefingVersion {
  version: number;
  at: string;
  run_id: string | null;
  run_kind: string | null;
  run_title: string;
  started_by: string;
  kinds: string[];
}

export const useBriefingVersions = (projectId: string | null) => {
  const http = useAgentsHttp();
  return useQuery<{ project_id: string; latest: number; versions: BriefingVersion[] }>(
    [QK.briefingVersions, projectId],
    () => http("/projects/briefing-versions", { project_id: projectId }),
    { enabled: !!projectId },
  );
};
